import React, { useState, useEffect, useRef } from 'react';
import useSidebarResizing from '../hooks/useSidebarResizing';
import { useDispatch, useSelector } from 'react-redux';
import DjChangeBanner from './DjChangeBanner';
import { wrappablePath } from '../utils/formatPath';
import { formatDjImportSummary, formatDjImportStopped } from '../utils/djImportSummary';
import SendToAppModal from './SendToAppModal';
import SyncFromAppModal from './SyncFromAppModal';
import { getImportExclusions, localPlaylistSelectionKey } from '../utils/djImportSelection';
import AutoSyncToggle from './AutoSyncToggle';
import DjLibraryMeter from './DjLibraryMeter';
import DjLocationControls from './DjLocationControls';
import { persistDjSidebarWidth, DJ_SIDEBAR_MIN_WIDTH, DJ_SIDEBAR_MAX_WIDTH } from '../redux/utils/djSidebarWidth';
import useAutoSyncNudge from '../hooks/useAutoSyncNudge';
import traktorLogo from '../assets/traktor-logo.png';
import { track } from '../utils/analytics';
import { clearSyncRequest } from '../redux/slices/djLibraryChangesSlice';
import { Button, Spinner, ListGroup, Badge } from 'react-bootstrap';
import ErrorBanner from './ErrorBanner';
import {
    loadPersistedTraktorState,
    toggleTraktorSidebar,
    setTraktorSidebarWidth,
    setTraktorSyncStatus,
    setTraktorPath,
    setTraktorExists,
    setTraktorPlaylists,
    setTraktorLastSyncTime,
    setTraktorImporting,
    setTraktorExporting,
    setTraktorError
} from '../redux/slices/traktorSlice';
import { triggerPlaylistRefresh } from '../redux/slices/playlistSlice';
import { useToast } from '../contexts/ToastContext';
import './TraktorSidebar.scss';
import { errMsg } from '../redux/utils/errMsg';

const TraktorSidebar = () => {
    const dispatch = useDispatch();
    const toast = useToast();
    const {
        sidebarOpen,
        sidebarWidth,
        syncStatus,
        traktorPath,
        traktorExists,
        traktorPlaylists,
        lastSyncTime,
        importing,
        exporting,
        error
    } = useSelector(state => state.traktor);

    const authUserId = useSelector((state) => state.auth?.user?.id) || null;
    const maybeNudgeAutoSync = useAutoSyncNudge('traktor', { label: 'Traktor', logo: traktorLogo });
    const [isResizing, setIsResizing] = useState(false);
    useSidebarResizing(isResizing);
    const sidebarRef = useRef(null);
    const widthRef = useRef(sidebarWidth);
    widthRef.current = sidebarWidth;
    const [hasCheckedTraktor, setHasCheckedTraktor] = useState(false);
    const [hasLoadedPlaylists, setHasLoadedPlaylists] = useState(false);
    const [traktorVersion, setTraktorVersion] = useState(null);

    // Load persisted state on mount
    useEffect(() => {
        const loadAsync = async () => {
            await new Promise(resolve => setTimeout(resolve, 0));

            try {
                // "Last synced" is not read here: it belongs to the account and is
                // hydrated from the main process by DjSyncStateHydrator.
                const traktorPath = localStorage.getItem('traktor.path') || null;
                const traktorPlaylists = JSON.parse(localStorage.getItem('traktor.playlists')) || [];

                dispatch(loadPersistedTraktorState({
                    traktorPath,
                    traktorPlaylists
                }));
            } catch (e) {
                console.error('Failed to load persisted traktor state:', e);
                dispatch(loadPersistedTraktorState({}));
            }
        };
        loadAsync();
    }, [dispatch]);

    // Save state on app close
    useEffect(() => {
        const saveOnClose = () => {
            try {
                localStorage.setItem('traktor.path', traktorPath);
                localStorage.setItem('traktor.playlists', JSON.stringify(traktorPlaylists));
            } catch (e) {
                // Silently fail
            }
        };

        window.addEventListener('beforeunload', saveOnClose);
        return () => window.removeEventListener('beforeunload', saveOnClose);
    }, [traktorPath, traktorPlaylists]);

    // Load imported playlists from database
    const loadImportedPlaylists = async (forceRefresh = false) => {
        if (!forceRefresh && traktorPlaylists.length > 0) {
            return traktorPlaylists;
        }

        try {
            // Pass `false` — display only needs name + trackCount. Asking
            // for tracks here would IPC every track in every playlist.
            const allPlaylists = await window.electron.db.playlists.getAll(false);
            const traktorImported = allPlaylists.filter(p =>
                (p.apps && p.apps.includes('traktor')) || (p.description && p.description.includes('Imported from Traktor'))
            );
            dispatch(setTraktorPlaylists(traktorImported));
            return traktorImported;
        } catch (err) {
            console.error('[TraktorSidebar] Error loading imported playlists:', err);
            return [];
        }
    };

    // On sidebar open
    useEffect(() => {
        if (!sidebarOpen) return;

        // Skip DB fetch if we already checked (even if no playlists found)
        if (hasLoadedPlaylists || traktorPlaylists.length > 0) {
            if (syncStatus !== 'partial') dispatch(setTraktorSyncStatus(traktorPlaylists.length > 0 ? 'synced' : 'not_synced'));

            if (!hasCheckedTraktor) {
                checkTraktor();
                setHasCheckedTraktor(true);
            }
            return;
        }

        const initialize = async () => {
            if (!hasCheckedTraktor) {
                dispatch(setTraktorSyncStatus('checking'));
            }

            // Run both checks in parallel for faster load
            const [playlists] = await Promise.all([
                loadImportedPlaylists(),
                !hasCheckedTraktor ? checkTraktor() : Promise.resolve()
            ]);

            setHasLoadedPlaylists(true);

            if (!hasCheckedTraktor) {
                setHasCheckedTraktor(true);
            }

            if (syncStatus !== 'partial') dispatch(setTraktorSyncStatus(playlists.length > 0 ? 'synced' : 'not_synced'));
        };

        initialize();
    }, [sidebarOpen]);

    // Handle resize
    useEffect(() => {
        if (!isResizing) return;

        const handleMouseMove = (e) => {
            // The sidebar sits right of the icon rail (position:fixed with a
            // left offset) — width is cursor minus its left edge, not raw clientX.
            const left = sidebarRef.current?.getBoundingClientRect().left ?? 0;
            const newWidth = e.clientX - left;
            if (newWidth >= DJ_SIDEBAR_MIN_WIDTH && newWidth <= DJ_SIDEBAR_MAX_WIDTH) {
                dispatch(setTraktorSidebarWidth(newWidth));
            }
        };

        const handleMouseUp = () => {
            setIsResizing(false);
            persistDjSidebarWidth(widthRef.current);
        };

        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);

        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
    }, [isResizing, dispatch]);

    const checkTraktor = async () => {
        try {
            const result = await window.electron.traktor.checkExists();
            dispatch(setTraktorPath(result.path));
            dispatch(setTraktorExists(result.exists));
            setTraktorVersion(result.version);
            return !!result.exists;
        } catch (err) {
            console.error('Error checking Traktor:', err);
            dispatch(setTraktorError(err.message));
            return false;
        }
    };

    // Re-scan must SAY what it found — a re-check that ends in the same
    // "not found" card changes nothing on screen and reads as a dead button.
    const rescanTraktor = async () => {
        const found = await checkTraktor();
        if (found) toast.success('Traktor collection found');
        else toast.info('Still no Traktor collection in the usual folders — if yours lives somewhere else, use Locate\u2026');
    };

    // opts.playlistKeys — explicit picker selection (SyncFromAppModal).
    // Without it, the persisted exclusions apply, so one-click re-syncs
    // never re-import playlists the user unchecked.
    const handleImport = async (opts = {}) => {
        const playlistKeys = Array.isArray(opts.playlistKeys) ? opts.playlistKeys : null;
        const excludeKeys = playlistKeys ? [] : getImportExclusions('traktor', authUserId);
        const wasFirstSync = !lastSyncTime; // captured before setTraktorLastSyncTime below
        dispatch(setTraktorImporting(true));
        const importStartedAt = Date.now();
        try {
            const result = await window.electron.traktor.importPlaylists({
                playlistKeys: playlistKeys || undefined,
                excludeKeys: excludeKeys.length ? excludeKeys : undefined,
            });

            // Stopped from the picker: playlists imported before the stop are
            // COMPLETE (each commits together with its tracks); the rest —
            // and the sync-complete mark — wait for the next run.
            if (result?.canceled) {
                dispatch(triggerPlaylistRefresh());
                const kept = await loadImportedPlaylists(true);
                // A stop that changed nothing leaves the previous state true:
                // a synced library stays "synced", not "incomplete". Only a
                // run that actually landed content degrades the badge.
                const stopChanged = (result.imported ?? 0) + (result.updated ?? 0) + (result.tracksCreated ?? 0) + (result.tracksUpdated ?? 0) > 0;
                if (stopChanged || syncStatus !== 'synced') dispatch(setTraktorSyncStatus(kept.length ? 'partial' : 'not_synced'));
                track('dj_import_canceled', {
                    app: 'traktor',
                    playlists_imported: result.imported ?? 0,
                    tracks_created: result.tracksCreated ?? 0,
                    tracks_updated: result.tracksUpdated ?? 0,
                    duration_ms: Date.now() - importStartedAt,
                });
                toast.info(formatDjImportStopped(result));
                return;
            }

            dispatch(setTraktorLastSyncTime(new Date().toISOString()));
            dispatch(setTraktorSyncStatus('synced'));
            dispatch(triggerPlaylistRefresh());

            await loadImportedPlaylists(true);

            track('dj_playlists_imported', {
                app: 'traktor',
                mode: playlistKeys ? 'selected' : 'all',
                excluded_count: excludeKeys.length,
                imported: result.imported ?? 0,
                updated: result.updated ?? 0,
                tracks_created: result.tracksCreated ?? 0,
                tracks_updated: result.tracksUpdated ?? 0,
                from: opts.from || 'sidebar',
                duration_ms: Date.now() - importStartedAt,
            });
            if (!maybeNudgeAutoSync(result, opts.from, { firstSync: wasFirstSync })) {
                toast.success(formatDjImportSummary(result, { app: 'Traktor' }));
            }
        } catch (err) {
            console.error('Error importing from Traktor:', err);
            toast.error(`Import failed: ${errMsg(err)}`);
            // Playlists commit whole, so a failure can still leave complete
            // imports behind — keep them visible; the badge says incomplete.
            const kept = await loadImportedPlaylists(true);
            dispatch(setTraktorSyncStatus(kept.length ? 'partial' : 'error'));
            dispatch(setTraktorError(err.message));
        } finally {
            dispatch(setTraktorImporting(false));
        }
    };

    // "Sync now" from the toast card / OS notification opens this sidebar and
    // asks it to import once it's mounted.
    const [showSend, setShowSend] = useState(false);
    const [showImportPicker, setShowImportPicker] = useState(false);

    // Local playlists are owner-scoped (migration 012): on account switch or
    // session restore, drop the previous account's cached list and refetch.
    const prevUserRef = useRef(authUserId);
    useEffect(() => {
        if (prevUserRef.current === authUserId) return;
        prevUserRef.current = authUserId;
        dispatch(setTraktorPlaylists([]));
        if (sidebarOpen) loadImportedPlaylists(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [authUserId]);
    const syncRequested = useSelector((state) => !!state.djLibraryChanges?.syncRequested?.traktor);
    const pendingDiff = useSelector((state) => state.djLibraryChanges?.pending?.traktor?.diff);
    useEffect(() => {
        if (!syncRequested) return;
        dispatch(clearSyncRequest('traktor'));
        // A change that includes never-imported playlists is a choice, not a
        // one-click action — open the picker (new rows start unchecked) so
        // hundreds of unimported playlists can't flood in behind one click.
        // Changes to already-synced playlists alone stay one-click.
        if (pendingDiff?.newPlaylists?.length) setShowImportPicker(true);
        else handleImport({ from: 'watcher_card' });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [syncRequested]);

    const handleExport = async () => {
        let playlistsToExport = [];
        try {
            const allPlaylists = await window.electron.db.playlists.getAll(true);
            playlistsToExport = allPlaylists;
        } catch (err) {
            console.error('Error fetching playlists:', err);
            toast.error('Failed to fetch playlists');
            return;
        }

        if (playlistsToExport.length === 0) {
            toast.warning('No playlists to export');
            return;
        }

        const totalTracks = playlistsToExport.reduce((sum, p) => sum + (p.tracks?.length || 0), 0);

        const result = await window.electron.dialog.showSaveDialog({
            title: 'Export playlists for Traktor',
            defaultPath: 'traktor-collection.nml',
            filters: [{ name: 'Traktor NML Files', extensions: ['nml'] }]
        });

        if (result.canceled || !result.filePath) return;

        const confirmed = await toast.confirm(
            `This will export ${playlistsToExport.length} playlist${playlistsToExport.length !== 1 ? 's' : ''} with ${totalTracks} track${totalTracks !== 1 ? 's' : ''} to:\n\n${result.filePath}\n\nTo use in Traktor:\nImport the NML file via Traktor's settings.`,
            'Export for Traktor',
            { confirmText: 'Export', confirmVariant: 'primary' }
        );

        if (!confirmed) return;

        dispatch(setTraktorExporting(true));
        try {
            await window.electron.traktor.exportPlaylists(playlistsToExport, result.filePath);
            toast.success(`Exported ${playlistsToExport.length} playlist${playlistsToExport.length !== 1 ? 's' : ''} to ${result.filePath}`);
        } catch (err) {
            console.error('Error exporting playlists:', err);
            toast.error(`Export failed: ${errMsg(err)}`);
        } finally {
            dispatch(setTraktorExporting(false));
        }
    };

    const formatSyncTime = (isoString) => {
        if (!isoString) return 'Never';
        const date = new Date(isoString);
        // Persisted "null"/garbage (fresh profiles) must not show "Invalid Date".
        return isNaN(date.getTime()) ? 'Never' : date.toLocaleString();
    };

    if (!sidebarOpen) return null;

    // Which imported playlists the user excluded from sync — kept locally by
    // design, skipped by re-syncs. localStorage read per render is cheap; the
    // Manage-imports picker stays the source of truth.
    const excludedKeySet = new Set(getImportExclusions('traktor', authUserId));
    const isExcludedFromSync = (p) => { const k = localPlaylistSelectionKey('traktor', p); return !!k && excludedKeySet.has(k); };

    return (
        <div ref={sidebarRef} className={`traktor-sidebar${isResizing ? ' resizing' : ''}`} style={{ width: `${sidebarWidth}px` }}>
            <SendToAppModal app="traktor" show={showSend} onClose={() => setShowSend(false)} onSent={() => loadImportedPlaylists?.(true)} />
            <SyncFromAppModal app="traktor" show={showImportPicker} onClose={() => setShowImportPicker(false)} userId={authUserId} onImport={(keys) => handleImport({ playlistKeys: keys, from: 'picker' })} />
            <div className="sidebar-header">
                <div className="header-title">
                    <h4>Traktor Sync</h4>
                    <button
                        className="sidebar-collapse-btn"
                        onClick={() => dispatch(toggleTraktorSidebar())}
                        title="Hide Traktor"
                    >
                        ‹
                    </button>
                </div>

                <div className="sync-status">
                    {syncStatus === 'checking' && (
                        <Badge bg="secondary">
                            <Spinner animation="border" size="sm" /> Checking...
                        </Badge>
                    )}
                    {syncStatus === 'synced' && <Badge bg="success">Playlists imported</Badge>}
                    {/* Stopped or failed mid-import: whole playlists are in
                        (each commits with its tracks) but the mirror isn't
                        complete yet — neither "imported" nor "no playlists"
                        is true, so say the third thing. */}
                    {syncStatus === 'partial' && <Badge bg="warning" text="dark">Sync incomplete</Badge>}
                    {/* "Not Synced" measured PLAYLISTS but sat beside a track
                        count, so a library with thousands of tracks imported
                        read as not synced at all — two true statements that
                        looked like a contradiction. Say which one this is. */}
                    {syncStatus === 'not_synced' && traktorExists && (
                        <Badge bg="secondary">No playlists imported</Badge>
                    )}
                    {syncStatus === 'not_synced' && !traktorExists && (
                        <Badge bg="secondary">Not Found</Badge>
                    )}
                    {syncStatus === 'error' && <Badge bg="danger">Error</Badge>}
                </div>
            </div>

            <div className="sidebar-content">
                {(syncStatus === 'synced' || syncStatus === 'partial') ? (
                    <div className="synced-view">
                        <div className="sync-info">
                            <DjChangeBanner app="traktor" onSync={() => handleImport({ from: 'banner' })} syncing={importing} />
                            <p className="last-sync">Last synced: {formatSyncTime(lastSyncTime)}</p>
                            <p className="traktor-path" title={traktorPath}>{wrappablePath(traktorPath)}</p>
                        </div>

                        <AutoSyncToggle app="traktor" label="Traktor" />

                        <DjLibraryMeter app="traktor" exists={traktorExists} userId={authUserId} importing={importing} onManage={() => setShowImportPicker(true)} />

                        <div className="section">
                            <h5>Imported Playlists ({traktorPlaylists.length})</h5>
                            {traktorPlaylists.length > 0 ? (
                                <ListGroup variant="flush" className="playlist-list">
                                    {traktorPlaylists.map((playlist) => (
                                        <ListGroup.Item key={playlist.id} className="playlist-item">
                                            <span className="playlist-name">{playlist.name}</span>
                                            {isExcludedFromSync(playlist) && <span className="dj-not-syncing-tag">not syncing</span>}
                                            <Badge bg="secondary" className="track-count">
                                                {playlist.trackCount || 0} tracks
                                            </Badge>
                                        </ListGroup.Item>
                                    ))}
                                </ListGroup>
                            ) : (
                                <p className="empty-message">No playlists imported yet</p>
                            )}
                        </div>

                        <div className="action-buttons">
                            <Button
                                variant="outline-primary"
                                onClick={() => handleImport({ from: 'resync' })}
                                disabled={importing}
                                className="action-btn"
                            >
                                {importing ? (
                                    <><Spinner animation="border" size="sm" /> Re-importing...</>
                                ) : (
                                    'Re-sync from Traktor'
                                )}
                            </Button>
                            <Button
                                variant="outline-secondary"
                                onClick={() => setShowImportPicker(true)}
                                disabled={importing}
                                className="action-btn"
                            >
                                Manage imports…
                            </Button>
                            <Button
                                variant="outline-secondary"
                                onClick={() => setShowSend(true)}
                                className="action-btn"
                            >
                                Send playlists to Traktor…
                            </Button>
                        </div>
                    </div>
                ) : (
                    <div className="not-synced-view">
                        <div className="section detection-section">
                            <h5>Traktor Library</h5>
                            {traktorExists ? (
                                <div className="detected">
                                    <div className="status-icon success">&#10003;</div>
                                    <div className="status-text">
                                        <p className="status-label">Traktor library found</p>
                                        <p className="traktor-path" title={traktorPath}>{wrappablePath(traktorPath)}</p>
                                        {traktorVersion && <p className="version">{traktorVersion}</p>}
                                    </div>
                                </div>
                            ) : (
                                <div className="not-detected">
                                    <div className="status-icon warning">!</div>
                                    <div className="status-text">
                                        <p className="status-label">Traktor not found</p>
                                        <p className="hint">Make sure Traktor is installed</p>
                                    </div>
                                </div>
                            )}
                            <div className="detection-actions">
                                {/* Re-scan is a retry for FAILED detection — once the
                                    library is found it's noise next to "Change
                                    location…" (two look-alike text links). */}
                                {!traktorExists && (
                                    <Button
                                        variant="link"
                                        size="sm"
                                        onClick={rescanTraktor}
                                        className="refresh-btn"
                                        title="Search the usual locations for your Traktor library again"
                                    >
                                        Re-scan
                                    </Button>
                                )}
                                <DjLocationControls app="traktor" label="Traktor" exists={hasCheckedTraktor ? traktorExists : null} onChanged={checkTraktor} />
                            </div>
                        </div>

                        <div className="section import-section">
                            <h5>Import from Traktor</h5>
                            <div className="safety-warning">
                                <span className="warning-icon">&#9432;</span>
                                <span>Reads your Traktor collection. Your library will NOT be modified.</span>
                            </div>

                            <DjLibraryMeter compact app="traktor" exists={traktorExists} userId={authUserId} />

                            <Button
                                variant="primary"
                                onClick={() => setShowImportPicker(true)}
                                disabled={!traktorExists || importing}
                                className="action-btn import-btn"
                            >
                                {importing ? (
                                    <><Spinner animation="border" size="sm" /> Importing...</>
                                ) : (
                                    'Import from Traktor'
                                )}
                            </Button>
                        </div>

                        <div className="section export-section">
                            <h5>Send to Traktor</h5>
                            <p className="section-hint">Put MixGoat playlists into your Traktor library — pick which ones, only local files are sent.</p>
                            <Button variant="secondary" onClick={() => setShowSend(true)} className="action-btn export-btn">
                                Send playlists to Traktor…
                            </Button>
                        </div>

                    </div>
                )}
                {/* Both views: a failed re-sync must not hide the imported
                    playlists it didn't touch. */}
                {error && (
                    <ErrorBanner
                        variant="inline"
                        error={error}
                        surface="traktor_sync"
                        onRetry={() => {
                            dispatch(setTraktorError(null));
                            if (syncStatus === 'error' || syncStatus === 'partial') handleImport();
                            else checkTraktor();
                        }}
                    />
                )}
            </div>

            <div className="sidebar-resizer" onMouseDown={() => setIsResizing(true)} />
        </div>
    );
};

export default TraktorSidebar;
